import { Button, Collapse, Skeleton } from 'antd'
import CantileverCard from 'components/CantileverCard'
import { RolodexEntryQueryBody } from 'features/rolodex'
import React, { FC, useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'
import { RolodexEntryType } from 'types'

import {
  PaginatedCollapsibleLinkedItemListProps,
  usePaginatedCollapsibleLinkedItemList,
} from './LinkedItemList'

const StyledCollapse = styled(Collapse)`
  .ant-collapse-item {
    border-radius: 0.375rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .ant-collapse-header {
    font-weight: 600;
    color: ${CantileverTheme.colors.gray['800']};
    border-radius: 0.375rem 0.375rem 0 0 !important;

    &:hover {
      background: ${CantileverTheme.colors.grayscale['500']};
    }
  }

  .ant-collapse-content-box {
    padding: 1rem;
  }
`

const ExpandButton = styled(Button)`
  font-size: 0.75rem;
  height: auto;
  padding: 0.25rem 0.5rem;
  border-radius: 0.375rem;
`

const LinkCount = styled.span`
  font-size: 0.75rem;
  padding: 0.125rem 0.375rem;
  border-radius: 0.75rem;
  font-weight: 500;
`

interface LinksSectionProps {
  title: string
  typeToQuery: Record<RolodexEntryType, RolodexEntryQueryBody>
  currentOrgId: string | undefined
  currentSiteId: string | undefined
  hasBottomMargin?: boolean
}

export const LinksSection: FC<LinksSectionProps> = ({
  title,
  typeToQuery,
  currentOrgId,
  currentSiteId,
  hasBottomMargin = false,
}) => {
  const [activeKeys, setActiveKeys] = useState<ReadonlyArray<string>>([])

  const allKeys = useMemo(() => Object.keys(typeToQuery), [typeToQuery])
  const isAllExpanded =
    allKeys.length > 0 && allKeys.every((key) => activeKeys.includes(key))
  const hasExpandableItems = allKeys.length > 0

  const handleExpandAll = useCallback(() => {
    if (isAllExpanded) {
      setActiveKeys([])
    } else {
      setActiveKeys(allKeys)
    }
  }, [isAllExpanded, allKeys])

  const handleCollapseChange = useCallback((keys: string | string[]) => {
    setActiveKeys(Array.isArray(keys) ? keys : [keys])
  }, [])

  const listProps = useMemo<
    ReadonlyArray<PaginatedCollapsibleLinkedItemListProps>
  >(
    () =>
      Object.entries(typeToQuery).map(([key, value]) => ({
        entryType: key as RolodexEntryType,
        query: value,
        currentOrgId,
        currentSiteId,
      })),
    [typeToQuery, currentOrgId, currentSiteId]
  )
  const collapseData = usePaginatedCollapsibleLinkedItemList(listProps)

  const entryCount = useMemo(
    () => collapseData.reduce((acc, item) => acc + item.totalEntries, 0),
    [collapseData]
  )

  const collapseItems = useMemo(() => {
    return collapseData
      .filter((item) => item.totalEntries > 0)
      .map((item) => ({
        key: item.key,
        label: item.label,
        children: item.children,
      }))
  }, [collapseData])

  const anyLoading = collapseData.some((item) => item.isLoading)

  const mutActiveKeys = useMemo(() => [...activeKeys], [activeKeys])
  return anyLoading ? (
    <Skeleton active paragraph={{ rows: 4 }} />
  ) : (
    <CantileverCard>
      <CantileverCard.CardHeader
        leadingContent={
          <span>
            {title} <LinkCount>{entryCount}</LinkCount>
          </span>
        }
        trailingContent={
          hasExpandableItems && (
            <ExpandButton type='text' size='small' onClick={handleExpandAll}>
              {isAllExpanded ? 'Collapse All' : 'Expand All'}
            </ExpandButton>
          )
        }
      />
      <CantileverCard.Section topPadding bottomPadding>
        <StyledCollapse
          size='small'
          destroyInactivePanel
          items={collapseItems}
          activeKey={mutActiveKeys}
          onChange={handleCollapseChange}
          style={{ marginBottom: hasBottomMargin ? '2rem' : '0' }}
        />
      </CantileverCard.Section>
    </CantileverCard>
  )
}
